// Centralized error handling utilities

export const ErrorTypes = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  AUTHENTICATION_ERROR: 'AUTHENTICATION_ERROR',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  PERMISSION_ERROR: 'PERMISSION_ERROR',
  SERVER_ERROR: 'SERVER_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR'
};

export const ErrorMessages = {
  [ErrorTypes.NETWORK_ERROR]: 'Network connection failed. Please check your internet connection.',
  [ErrorTypes.AUTHENTICATION_ERROR]: 'Authentication failed. Please log in again.',
  [ErrorTypes.VALIDATION_ERROR]: 'Please check your input and try again.',
  [ErrorTypes.PERMISSION_ERROR]: 'You do not have permission to perform this action.',
  [ErrorTypes.SERVER_ERROR]: 'Server error occurred. Please try again later.',
  [ErrorTypes.UNKNOWN_ERROR]: 'An unexpected error occurred. Please try again.'
};

export class AppError extends Error {
  constructor(message, type = ErrorTypes.UNKNOWN_ERROR, details = null) {
    super(message);
    this.name = 'AppError';
    this.type = type;
    this.details = details;
    this.timestamp = new Date().toISOString();
  }
}

/**
 * Format DRF-style 400 validation errors (field -> list of messages) into one user-facing string.
 * Supports ApiResponse envelope: when data.success === false, use data.message and data.errors.
 */
function formatValidationMessage(data) {
  if (!data || typeof data !== 'object') return 'Invalid request';
  if (data.success === false && typeof data.message === 'string') return data.message;
  if (typeof data.detail === 'string') return data.detail;
  if (typeof data.message === 'string') return data.message;
  const payload = data.errors != null ? data.errors : data;
  const parts = [];
  for (const [key, value] of Object.entries(payload)) {
    if (key === 'detail' || key === 'message' || key === 'success' || key === 'data') continue;
    const msg = Array.isArray(value) ? value[0] : value;
    if (msg != null && typeof msg === 'string') {
      const label = key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
      parts.push(`${label}: ${msg}`);
    }
  }
  return parts.length ? parts.join(' ') : 'Invalid request';
}

export const handleApiError = (error) => {
  console.error('API Error:', error);
  
  if (!error.response) {
    // Network error
    return new AppError(
      ErrorMessages[ErrorTypes.NETWORK_ERROR],
      ErrorTypes.NETWORK_ERROR,
      { originalError: error.message }
    );
  }
  
  const { status, data } = error.response;
  const message = data?.success === false ? data.message : null;
  const fallback = message ?? data?.detail ?? data?.message ?? (typeof data?.error === 'string' ? data.error : null);

  switch (status) {
    case 400:
      return new AppError(
        formatValidationMessage(data),
        ErrorTypes.VALIDATION_ERROR,
        { status, data }
      );
    case 401:
      return new AppError(
        fallback || 'Authentication required',
        ErrorTypes.AUTHENTICATION_ERROR,
        { status, data }
      );
    case 403:
      return new AppError(
        fallback || 'Access denied',
        ErrorTypes.PERMISSION_ERROR,
        { status, data }
      );
    case 404:
      return new AppError(
        fallback || 'Resource not found',
        ErrorTypes.VALIDATION_ERROR,
        { status, data }
      );
    case 500:
    case 502:
    case 503:
    case 504:
      return new AppError(
        fallback || ErrorMessages[ErrorTypes.SERVER_ERROR],
        ErrorTypes.SERVER_ERROR,
        { status, data }
      );
    default:
      return new AppError(
        fallback || ErrorMessages[ErrorTypes.UNKNOWN_ERROR],
        ErrorTypes.UNKNOWN_ERROR,
        { status, data }
      );
  }
};

export const handleImageUploadError = (error) => {
  console.error('Image Upload Error:', error);
  
  if (error.code === 'FILE_TOO_LARGE') {
    return new AppError(
      'File is too large. Please select a smaller image.',
      ErrorTypes.VALIDATION_ERROR,
      { maxSize: '5MB' }
    );
  }
  
  if (error.code === 'INVALID_FILE_TYPE') {
    return new AppError(
      'Invalid file type. Please select a valid image file.',
      ErrorTypes.VALIDATION_ERROR,
      { allowedTypes: ['jpg', 'jpeg', 'png', 'gif', 'webp'] }
    );
  }
  
  return handleApiError(error);
};

export const handleAdSenseError = (error) => {
  console.error('AdSense Error:', error);
  
  // Don't show AdSense errors to users as they're not actionable
  return null;
};

export const logError = (error, context = {}) => {
  const errorInfo = {
    message: error.message,
    type: error.type || ErrorTypes.UNKNOWN_ERROR,
    stack: error.stack,
    timestamp: error.timestamp || new Date().toISOString(),
    context,
    userAgent: navigator.userAgent,
    url: window.location.href
  };
  
  // In production, you might want to send this to an error tracking service
  console.error('Error Logged:', errorInfo);
  
  return errorInfo;
};

export const createErrorBoundary = (error, errorInfo) => {
  const errorBoundaryError = new AppError(
    'Something went wrong with this component',
    ErrorTypes.UNKNOWN_ERROR,
    { componentStack: errorInfo.componentStack }
  );
  
  logError(errorBoundaryError, { errorBoundary: true });
  return errorBoundaryError;
};
